/**
 * Full-height SOF review sheet: sticky progress header, statement rail (or
 * phone stepper), evidence workspace, and a footer with the confirm flow.
 * Owns the local review state; explicit Save sends `buildSofUpdate`.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Platform, ScrollView, StyleSheet, Text, View } from 'react-native';

import { reviewDesk } from '@/design-system';
import { useResponsive } from '@/hooks/useResponsive';
import { useTranslation } from '@/hooks/useTranslation';
import { getLevelForm, type StatementRatingValue } from '@/data/icfFormStatements';
import { trackAuthEvent } from '@/services/analytics';
import type {
  EngagementLanguage, EngagementLevel, EngagementSessionRow, EvidenceFeedbackReason, EvidenceFeedbackVerdict,
  SessionObservationForm, SessionTranscript, SofOverrideReason, SofUpdateRequest,
} from '@/types/mentorEngagements';
import { ActionButton } from '../shared';
import { buildSofUpdate, deriveReviewState, sofReviewSummary, type SofReviewState } from '../sofReview';
import { SofProgressHeader } from './SofProgressHeader';
import { SofStatementPane } from './SofStatementPane';
import { SofStatementRail, WRAPUP_ID } from './SofStatementRail';
import { SofWrapUpStep } from './SofWrapUpStep';
import { TranscriptSideSheet } from './TranscriptSideSheet';
import { buildReviewItems, buildTimeline, nextUnfinished, type EvidenceMoment } from './model';
import { useSofKeyboard } from './useSofKeyboard';

export interface SofReviewSheetProps {
  visible: boolean;
  level: EngagementLevel;
  language: EngagementLanguage;
  session: EngagementSessionRow;
  form: SessionObservationForm;
  /** Read-only transcript for Jump; undefined renders the "not available" state. */
  transcript?: SessionTranscript;
  readOnly: boolean;
  saving: boolean;
  onClose: () => void;
  onSave: (update: SofUpdateRequest) => void | Promise<void>;
  onFinalize: () => void;
  onReopen: () => void;
  onDownloadPdf: () => void;
}

function initialState(form: SessionObservationForm): SofReviewState {
  return { mentorRatings: {}, c1: {}, feedback: form.feedback, evidenceFeedback: {} };
}

export function SofReviewSheet({ visible, level, language, session, form, transcript, readOnly, saving, onClose, onSave, onFinalize, onReopen, onDownloadPdf }: SofReviewSheetProps) {
  const { t } = useTranslation();
  const { isPhone, isDesktop } = useResponsive();
  const catalog = getLevelForm(level);
  const [state, setState] = useState(() => initialState(form));
  const [error, setError] = useState(false);
  // Navigation/UI state lives outside the form reset so a save never snaps back to statement 1.
  const [selectedId, setSelectedId] = useState<string>(() => catalog.competencies[0]?.statements[0]?.id ?? WRAPUP_ID);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [transcriptTurn, setTranscriptTurn] = useState<number | null>(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  useEffect(() => { setState(initialState(form)); setError(false); }, [form]);

  const locked = readOnly || form.status === 'final';
  const disabled = locked || saving;
  const recorded = session.source === 'mcai';
  // Jump-to-transcript is only offered once a transcript is actually loaded; the
  // mentor-scoped transcript endpoint is not live yet, so today this stays false.
  const transcriptAvailable = recorded && Boolean(transcript?.turns?.length);
  const items = useMemo(() => buildReviewItems(level, form, state), [level, form, state]);
  const selectedIndex = items.findIndex(item => item.id === selectedId);
  const selected = selectedIndex >= 0 ? items[selectedIndex] : null;
  const mergedMentorRatings = useMemo(() => {
    const merged: SessionObservationForm['mentor_ratings'] = { ...form.mentor_ratings };
    for (const [id, rating] of Object.entries(state.mentorRatings)) { if (rating === null) delete merged[id]; else merged[id] = rating; }
    return merged;
  }, [form.mentor_ratings, state.mentorRatings]);
  const summary = sofReviewSummary({ ai_ratings: form.ai_ratings, mentor_ratings: mergedMentorRatings }, level);
  const dirty = Object.keys(state.mentorRatings).length > 0 || Object.keys(state.c1).length > 0
    || Object.keys(state.evidenceFeedback ?? {}).length > 0 || JSON.stringify(state.feedback) !== JSON.stringify(form.feedback);
  const rejectedCount = items.filter(item => item.railState === 'evidence_rejected').length;

  const mentorFor = (id: string) => (Object.prototype.hasOwnProperty.call(state.mentorRatings, id) ? state.mentorRatings[id] ?? undefined : form.mentor_ratings[id]);
  const changeRating = useCallback((id: string, value: StatementRatingValue, patch: Partial<NonNullable<SofReviewState['mentorRatings'][string]>> = {}) => {
    setState(current => {
      const previous = current.mentorRatings[id] ?? form.mentor_ratings[id];
      const aiValue = deriveReviewState(form.ai_ratings[id], undefined, level).aiValue;
      return { ...current, mentorRatings: { ...current.mentorRatings, [id]: {
        rating_label: value, ...(level === 'pcc' ? { observed: value === 'observed' } : {}),
        comment: previous?.comment ?? null,
        override_reason: value === aiValue ? null : previous?.override_reason ?? null,
        ...patch,
      } } };
    });
  }, [form, level]);
  const useAi = (id: string) => setState(current => {
    const next = { ...current.mentorRatings };
    if (form.mentor_ratings[id]) next[id] = null; else delete next[id];
    return { ...current, mentorRatings: next };
  });
  const setComment = (id: string, comment: string) => {
    const value = deriveReviewState(form.ai_ratings[id], mentorFor(id), level).value;
    if (value !== null) changeRating(id, value, { comment });
  };
  const setVerdict = (id: string, moment: EvidenceMoment, verdict: EvidenceFeedbackVerdict, reason?: EvidenceFeedbackReason) => {
    if (!moment.hasKey) return;
    setState(current => ({ ...current, evidenceFeedback: { ...(current.evidenceFeedback ?? {}), [moment.key]: {
      statement_id: id, citation_key: moment.key, polarity: moment.polarity, verdict, ...(verdict === 'not_relevant' && reason ? { reason } : {}),
    } } }));
    trackAuthEvent('sof_evidence_feedback', { verdict, reason: verdict === 'not_relevant' ? reason : undefined, engagement_id: session.engagement_id, session_id: session.id, statement_id: id, polarity: moment.polarity, level });
  };

  const goTo = useCallback((id: string) => setSelectedId(id), []);
  const advance = useCallback(() => goTo(nextUnfinished(items, selectedId) ?? WRAPUP_ID), [goTo, items, selectedId]);
  const move = useCallback((delta: 1 | -1) => {
    const order = [...items.map(item => item.id), WRAPUP_ID];
    const index = order.indexOf(selectedId);
    const next = order[Math.min(order.length - 1, Math.max(0, index + delta))];
    if (next) goTo(next);
  }, [goTo, items, selectedId]);

  const save = async () => {
    if (disabled) return false;
    setError(false);
    try { await onSave(buildSofUpdate(state, level)); return true; } catch { setError(true); return false; }
  };

  const review = selected ? deriveReviewState(form.ai_ratings[selected.id], mentorFor(selected.id), level) : null;
  const primary = (() => {
    if (!selected || !review || locked) return null;
    const rejected = selected.railState === 'evidence_rejected';
    if (review.state === 'ai_suggested' && !rejected) return { key: 'confirmNext' as const, enabled: !saving };
    if (review.state === 'confirmed' || review.state === 'overridden') return { key: 'saveNext' as const, enabled: !saving };
    return { key: 'rateNext' as const, enabled: false };
  })();
  const runPrimary = async () => {
    if (!selected || !review || !primary?.enabled) return;
    if (primary.key === 'confirmNext' && review.aiValue !== null) {
      changeRating(selected.id, review.aiValue);
      trackAuthEvent('sof_rating_confirmed', { engagement_id: session.engagement_id, session_id: session.id, statement_id: selected.id, level, mode: 'confirm' });
      advance();
      return;
    }
    if (primary.key === 'saveNext') {
      if (dirty && !(await save())) return;
      advance();
    }
  };

  const openTranscript = (moment?: EvidenceMoment) => {
    const turn = moment?.turn_index ?? buildTimeline(selected ? form.ai_ratings[selected.id] : undefined).find(item => item.turn_index != null)?.turn_index ?? null;
    setTranscriptTurn(turn); setTranscriptOpen(true);
  };

  useSofKeyboard({
    enabled: visible && !transcriptOpen && Platform.OS === 'web',
    onMove: move,
    onPick: index => { const option = catalog.scale.options[index]; if (selected && option && !disabled) changeRating(selected.id, option.value); },
    onConfirm: () => { void runPrimary(); },
    onToggleEvidence: () => { if (selected) setExpanded(current => ({ ...current, [selected.id]: !current[selected.id] })); },
    onTranscript: () => { if (transcriptAvailable) openTranscript(); },
  });

  const appendTimestamp = (timestamp: string) => {
    if (!selected) return;
    const comment = mentorFor(selected.id)?.comment ?? '';
    setComment(selected.id, comment ? `${comment} ${timestamp}` : timestamp);
  };

  const secondaryActions = (
    <>
      {!locked ? <ActionButton testID="sof_review_finalize" label={t('mentorEngagements.review.finalize')} icon="verified" disabled={saving || dirty} onPress={onFinalize} /> : null}
      {locked && form.status === 'final' ? <ActionButton testID="sof_review_reopen" label={t('mentorEngagements.review.reopen')} icon="lock-open" disabled={saving || readOnly} onPress={onReopen} /> : null}
      <ActionButton testID="sof_review_pdf" label={t('mentorEngagements.review.pdf')} icon="picture-as-pdf" disabled={saving || dirty} onPress={onDownloadPdf} />
    </>
  );

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={() => { if (!saving) onClose(); }}>
      <View style={[styles.backdrop, isPhone && styles.backdropPhone]}>
        <View testID="sof_review_modal" style={[styles.sheet, isPhone && styles.sheetPhone]}>
          <SofProgressHeader session={session} form={form} level={level} summary={summary} phone={isPhone} locked={locked} saving={saving} dirty={dirty} error={error} onClose={() => { if (!saving) onClose(); }} onSave={() => void save()} />
          <View style={[styles.body, isPhone && styles.bodyPhone]}>
            <SofStatementRail items={items} competencies={catalog.competencies} language={language} selectedId={selectedId} phone={isPhone} width={isDesktop ? 300 : 260} onSelect={goTo} />
            <ScrollView style={styles.paneScroll} contentContainerStyle={[styles.paneContent, isPhone && styles.paneContentPhone]}>
              {selected ? (
                <SofStatementPane
                  key={selected.id}
                  item={selected} level={level} language={language} form={form} state={state}
                  locked={locked} disabled={disabled} recorded={recorded} transcriptAvailable={transcriptAvailable}
                  expanded={Boolean(expanded[selected.id])}
                  onChangeRating={(id, value) => changeRating(id, value)}
                  onUseAi={useAi}
                  onOverrideReason={(id, reason: SofOverrideReason | null) => { const value = deriveReviewState(form.ai_ratings[id], mentorFor(id), level).value; if (value !== null) changeRating(id, value, { override_reason: reason }); }}
                  onComment={setComment}
                  onToggleExpanded={id => setExpanded(current => ({ ...current, [id]: !current[id] }))}
                  onVerdict={setVerdict}
                  onJump={openTranscript}
                />
              ) : (
                <SofWrapUpStep level={level} language={language} form={form} state={state} disabled={disabled} locked={locked} saving={saving} setState={setState} />
              )}
              {Platform.OS === 'web' && !isPhone ? <Text style={styles.keyboardHint}>{t('mentorEngagements.review.keyboard.hint')}</Text> : null}
            </ScrollView>
          </View>
          <View style={[styles.footer, isPhone && styles.footerPhone]}>
            {!locked && (summary.unrated > 0 || rejectedCount > 0) ? <Text style={styles.warning}>{t('mentorEngagements.review.finalizeWarning', { unrated: summary.unrated, rejected: rejectedCount })}</Text> : null}
            <View style={styles.footerRow}>
              <ActionButton testID="sof_review_prev" label={t('mentorEngagements.review.actions.prev')} icon="chevron-left" disabled={selectedIndex <= 0 && selected !== null} onPress={() => move(-1)} />
              {primary ? (
                <ActionButton testID="sof_review_confirm_next" label={t(`mentorEngagements.review.actions.${primary.key}`)} variant="primary" icon={primary.key === 'confirmNext' ? 'check' : 'chevron-right'} disabled={!primary.enabled} onPress={() => void runPrimary()} />
              ) : selected ? (
                <ActionButton testID="sof_review_confirm_next" label={t('mentorEngagements.review.actions.next')} icon="chevron-right" onPress={() => move(1)} />
              ) : null}
              {isPhone && !locked ? <ActionButton testID="sof_review_save" label={t('mentorEngagements.review.save')} variant={dirty ? 'primary' : 'secondary'} disabled={saving} onPress={() => void save()} /> : null}
              {!isPhone ? secondaryActions : null}
            </View>
            {isPhone ? <View style={styles.footerRow}>{secondaryActions}</View> : null}
          </View>
        </View>
      </View>
      <TranscriptSideSheet visible={transcriptOpen} transcript={transcript} centerTurn={transcriptTurn} phone={isPhone} onClose={() => setTranscriptOpen(false)} onCopyTimestamp={locked ? undefined : appendTimestamp} />
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(16, 24, 39, 0.45)', padding: 24 },
  backdropPhone: { padding: 0 },
  sheet: { flex: 1, backgroundColor: reviewDesk.card, borderRadius: 20, borderWidth: 1, borderColor: reviewDesk.line, overflow: 'hidden' },
  sheetPhone: { borderRadius: 0, borderWidth: 0 },
  body: { flex: 1, flexDirection: 'row', minHeight: 0 },
  bodyPhone: { flexDirection: 'column' },
  paneScroll: { flex: 1, minWidth: 0 },
  paneContent: { padding: 24, gap: 20, maxWidth: 760 },
  paneContentPhone: { padding: 16, maxWidth: undefined },
  keyboardHint: { color: reviewDesk.muted, fontSize: 11, marginTop: 8 },
  footer: { gap: 8, padding: 14, paddingHorizontal: 20, borderTopWidth: 1, borderTopColor: reviewDesk.line, backgroundColor: reviewDesk.cardMuted },
  footerPhone: { paddingHorizontal: 12 },
  footerRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' },
  warning: { color: '#8A5B00', fontSize: 12, fontWeight: '600' },
});
